import { useComparer } from '@/hooks/useComparer';
import { useEffect, useState } from 'react';

const CHUNK_SIZE = 50;

/**
 * Renders large comparer lists in chunks: the first chunk paints immediately and the
 * rest streams in on subsequent ticks until the whole list is in the DOM, keeping the
 * showUnchanged / expandAll toggles responsive without breaking ctrl+F (which needs
 * every row mounted, ruling out virtualization).
 */
export const useProgressiveList = <T>(items: T[], chunkSize = CHUNK_SIZE): T[] => {
  const { showUnchanged, expandAll } = useComparer();
  const [count, setCount] = useState<number>(chunkSize);
  const [previous, setPrevious] = useState({ showUnchanged, expandAll });

  // Reset during render (not in an effect) so a toggle only commits the first
  // chunk instead of committing the full list once before shrinking.
  if (previous.showUnchanged !== showUnchanged || previous.expandAll !== expandAll) {
    setPrevious({ showUnchanged, expandAll });
    setCount(chunkSize);
  }

  // Double the rendered count each tick: the first paint stays small while the
  // full list is reached in O(log n) ticks instead of a slow linear crawl.
  useEffect(() => {
    if (count >= items.length) return;
    const timeout = setTimeout(() => setCount((current) => current * 2), 0);
    return () => clearTimeout(timeout);
  }, [count, items.length]);

  return count < items.length ? items.slice(0, count) : items;
};
